import { Icon, type IconName } from '@/features/common/Icon';
import type { StatusKind } from '@/features/common/types/StatusKind';
import type { StatusLineProps } from '@/features/common/types/StatusLineProps';
import '@/features/common/status-line.css';

/** MUD gives each inline message its own glyph — see Components/messages-inline.html. */
const ICON: Record<StatusKind, IconName> = {
  success: 'checkmark-small',
  error: 'circle-error',
};

/**
 * The outcome of the last save or delete, announced — and absent while there is none.
 *
 * `message message--inline message--{kind}` is MUD's own inline message, not a class of our own:
 * the colour comes from the design system, so a failure cannot be painted in the success colour
 * by a screen that set the text and forgot the kind. `role="status"` is what makes a screen
 * reader announce it without moving focus, and every screen's tests address it through that role.
 */
export function StatusLine({ status, kind, className, id }: StatusLineProps) {
  if (!status) return null;
  const extra = className ? ` ${className}` : '';
  return (
    <p
      id={id}
      role="status"
      className={`message message--inline message--${kind} status-line${extra}`}
    >
      <Icon name={ICON[kind]} className="message__icon" />
      <span>{status}</span>
    </p>
  );
}
